import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { InputAdornment, OutlinedInput } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import "./ContactTable.css";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: "username",
    numeric: false,
    disablePadding: true,
    label: "Name",
  },
  {
    id: "mobile_no",
    numeric: false,
    disablePadding: false,
    label: "Mobile No",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "Email",
  },
  {
    id: "pickupLocation",
    numeric: false,
    disablePadding: false,
    label: "Pickup Location",
  },
  {
    id: "dropLocation",
    numeric: false,
    disablePadding: false,
    label: "Drop Location ",
  },
  {
    id: "tripType",
    numeric: false,
    disablePadding: false,
    label: "Trip Type",
  },
  {
    id: "carType",
    numeric: false,
    disablePadding: false,
    label: "Car Type",
  },
  {
    id: "date",
    numeric: false,
    disablePadding: false,
    label: "Date",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell, index) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ paddingLeft: index === 0 ? 2 : 2 }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function UserTableToolbar({ filterName, onFilterName }) {
  return (
    <Toolbar
      sx={{
        height: 80,
        display: "flex",
        justifyContent: "space-between",
        p: (theme) => theme.spacing(0, 3, 0, 2),
        pb: 2,
      }}
    >
      <OutlinedInput
        value={filterName}
        onChange={onFilterName}
        placeholder="Search user..."
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
      />
      <Tooltip title="Filter list">
        <IconButton>
          <FilterListIcon />
        </IconButton>{" "}
      </Tooltip>
    </Toolbar>
  );
}

export function applyFilter({ inputData, comparator, filterName }) {
  const stabilizedThis = inputData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    inputData = inputData.filter(
      (user) =>
        user?.username?.toLowerCase().indexOf(filterName.toLowerCase()) !==
          -1 ||
        user?.mobile_no
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.email
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.pickupLocation
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.dropLocation
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.tripType
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.carType
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1 ||
        user?.date
          ?.toString()
          .toLowerCase()
          .indexOf(filterName.toLowerCase()) !== -1
    );
  }

  return inputData;
}

export default function EnhancedTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("username");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [filterName, setFilterName] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [Iserror, setIserror] = React.useState(false);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://indoreairportcabbooking.com/api/contactdetails",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const responseData = await response.json();
        setIserror(false);
        setRows(responseData);
      } catch (error) {
        setIserror(true);
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const dataFiltered = applyFilter({
    inputData: rows,
    comparator: getComparator(order, orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length && !!filterName;

  const visibleRows = React.useMemo(
    () =>
      dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [dataFiltered, page, rowsPerPage]
  );

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", p: 2 }}>
      <Typography
        variant="h3"
        sx={{
          mb: 4,
          mt: 2,
          color: "#000000",
          fontWeight: "900",
          textDecoration: "underline",
          textDecorationThickness: "5px",
          textUnderlineOffset: "10px",
        }}
      >
        Indore Airport Cab Booking Details
      </Typography>
      <Paper sx={{ width: "100%", mb: 2, p: 3, backgroundColor: "#ffffff" }}>
        <UserTableToolbar
          filterName={filterName}
          onFilterName={handleFilterByName}
        />

        <TableContainer
          sx={{ height: 380, overflowY: "auto" }}
          className="custom-scrollbar"
        >
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      sx={{ paddingLeft: 2 }}
                    >
                      {row.username}
                    </TableCell>
                    <TableCell align="left">{row.mobile_no}</TableCell>
                    <TableCell align="left">{row.email}</TableCell>
                    <TableCell align="left">{row.pickupLocation}</TableCell>
                    <TableCell align="left">{row.dropLocation}</TableCell>
                    <TableCell align="left">{row.tripType}</TableCell>
                    <TableCell align="left">{row.carType}</TableCell>
                    <TableCell align="left">{row.date}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={8} />
                </TableRow>
              )}

              {Iserror && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ pt: 20 }}>
                    <Typography variant="h6" color="textSecondary">
                      Something went wrong
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {notFound && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ pt: 20 }}>
                    <Typography variant="h6" color="textSecondary">
                      No records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={dataFiltered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
